import { checkCookieExists, getCookieValue } from '@utils/web-storage';
import { AxiosInstance } from 'axios';
import { getNewAccessToken } from '@api/login';
import { redirect } from '@utils/client';
import routerPromise from '@router';
import { useAppStore } from '@stores/app';

const retryMap = new Map<string, boolean>();
let isRefreshing = false;
let subscribers: Array<(args: string) => void> = [];

function addSubscriber(cb: (args: string) => void): void {
	subscribers.push(cb);
}

function onAccessTokenFetched(token: string): void {
	subscribers = subscribers.filter((callback) => callback(token));
}

/**
 * When a request fails with a 401 status, it’s the result of an expired access token.
 * This interceptor attempts to refresh a user’s expired token and automatically retries the request.
 * If it fails, they are automatically directed back to login.
 * Required for authenticated endpoints.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function oAuthTokenRefresh(err: any, instance: AxiosInstance): Promise<unknown> {
	const appStore = useAppStore();
	const { config } = err;
	const status = err?.response?.status;
	const originalRequest = config;
	const userHasRefreshToken = checkCookieExists('userLoggedIn');
	const isMobileWebview = appStore.isMobileWebview;

	if (status !== 401) {
		return Promise.reject(err);
	}

	const router = await routerPromise;
	const minTokenScopeRequiredOnCurrentRoute = router.currentRoute.value.meta?.minTokenScopeRequired;
	if (minTokenScopeRequiredOnCurrentRoute === 'PRIVILEGED_ACCESS') {
		router.go(0); // re-run global route logic for PRIVILEGED_ACCESS since you can't refresh it
	}

	if (!isRefreshing) {
		if (isMobileWebview) {
			isRefreshing = true;
			const accessToken = getCookieValue('access_token');
			if (!accessToken || appStore.oauthToken === accessToken || retryMap.get(originalRequest.url)) {
				appStore.revokeMobileWebviewToken();
				redirect('/forbidden');
			} else {
				appStore
					.authenticateFromMobileWebview()
					.then(() => {
						isRefreshing = false;
						onAccessTokenFetched(accessToken);
					})
					.catch(() => {
						redirect('/forbidden');
					});
			}
		} else if (!userHasRefreshToken || retryMap.get(originalRequest.url)) {
			redirect('/login?message=expired-session');
			return Promise.reject(err);
		} else {
			isRefreshing = true;
			getNewAccessToken()
				.then((refreshToken) => {
					appStore.setUserAuthData(refreshToken);
					isRefreshing = false;
					onAccessTokenFetched(refreshToken.access_token);
				})
				.catch(() => {
					redirect('/login?message=expired-session');
					return Promise.reject(err);
				});
		}
	}

	return new Promise((resolve, reject) => {
		retryMap.set(originalRequest.url, true);
		originalRequest.isRetry = true;
		addSubscriber((token: string) => {
			originalRequest.headers.authorization = `Bearer ${token}`;
			instance(originalRequest).then(resolve).catch(reject);
		});
	});
}

export function attachOAuthTokenRefreshInterceptor(instance: AxiosInstance): number {
	return instance.interceptors.response.use(
		(response) => response,
		(error) => oAuthTokenRefresh(error, instance)
	);
}
